import * as React from "react";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  defaultAxisLabels,
  defaultExport,
  defaultLabel,
  defaultTooltipSize,
  defaultStyle,
  centerHorizontalBarTooltip,
  pieChartLabels,
} from "../../../../utils/charts";
import colors from "../../../../styles/colors";
import {
  generateSeries,
  calculateWidth,
  labelColor,
  defaultBarTooltip,
  createGradient,
} from "../../../../formatters/analytics";
import {
  AssessmentChartFilter,
  AssessmentChartFilterLabels,
  AssessmentStatus,
  AssessmentType,
} from "../../../../App/Analytics/Common";
import { AssessmentsChartContainer, PieChartContainer, BarChartContainer } from "./AssessmentAnalytics.styles";

interface IAssessmentsGraphProps {
  selectedFilters: AssessmentChartFilterLabels[];
  assessmentTotals: IAssessmentTotals;
  filterFunction: AssessmentChartFilter;
  downloadCSV: () => void;
  downloadXLSX: () => void;
}

interface IAssessmentPieChartProps {
  assessmentTotals: IAssessmentTotals;
}

interface IAssessmentBarChartProps {
  assessmentTotals: IAssessmentTotals;
  downloadCSV: () => void;
  downloadXLSX: () => void;
  filterFunction: AssessmentChartFilter;
}

export interface IAssessmentTotals {
  totalAssessments: number;
  totalPreAssigmentsTaken: number;
  totalPreAssigmentsTakenPassedOnFirst: number;
  totalPreAssigmentsNotTaken: number;
  totalPreAssigmentsNotTakenPassedOnFirstPostAssessment: number;
}

const AssessmentPieChart: React.SFC<IAssessmentPieChartProps> = ({ assessmentTotals }) => {
  const { totalAssessments, totalPreAssigmentsTaken, totalPreAssigmentsNotTaken } = assessmentTotals;

  const pieData = [
    {
      name: "Lesson First",
      y: totalPreAssigmentsNotTaken,
      /* tslint:disable-next-line */
      color: createGradient(colors.chartLightBlue100, colors.chartLightBlue70),
      ...pieChartLabels,
    },
    {
      name: "Pre-Assessment First",
      y: totalPreAssigmentsTaken,
      /* tslint:disable-next-line */
      color: createGradient(colors.chartLightGrey100, colors.chartLightGrey70),
      ...pieChartLabels,
    },
  ];

  const pieChartOptions: Highcharts.Options = {
    ...defaultStyle,
    chart: {
      type: "pie",
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        innerSize: "70%",
        showInLegend: false,
        size: "100%",
      },
    },
    responsive: {
      rules: [
        {
          chartOptions: {
            legend: {
              enabled: true,
            },
          },
          condition: {
            maxWidth: 500,
          },
        },
      ],
    },
    series: [
      {
        name: "Total Assessments",
        /* tslint:disable-next-line */
        data: [
          {
            name: "Totals",
            y: totalAssessments,
            // set series as transparent
            /* tslint:disable-next-line */
            color: "#00000000",
            dataLabels: {
              enabled: true,
              /* tslint:disable-next-line */
              distance: "-100%",
              format: "{point.total}",
              ...defaultLabel,
            },
          },
        ],
      },
      {
        name: "Assessments",
        /* tslint:disable-next-line */
        data: pieData,
      },
    ] as Highcharts.SeriesPieOptions[],
    title: {
      text: "",
    },
    tooltip: {
      enabled: true,
      outside: true,
      shape: "callout",
      formatter() {
        if (this.key === "Totals") {
          return false;
        }
        return defaultBarTooltip(this.color, this.key, this.percentage, this.y!);
      },
      ...defaultTooltipSize,
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={pieChartOptions}
      containerProps={{ style: { width: "100%", height: 150, position: "absolute", top: 50 } }}
    />
  );
};

export const AssessmentBarChart: React.FC<IAssessmentBarChartProps> = ({
  assessmentTotals,
  downloadCSV,
  downloadXLSX,
  filterFunction,
}) => {
  const totalHeight = 300;
  // these additional height & margin are added to reduce the space between bars
  // so that they look closer to each other
  const additionalHeight = 60;
  const topMargin = 25;
  // preventing export menu from overlapping chart
  const rightMargin = 50;

  const {
    totalAssessments,
    totalPreAssigmentsTaken,
    totalPreAssigmentsTakenPassedOnFirst,
    totalPreAssigmentsNotTaken,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
  } = assessmentTotals;

  const passedData = [totalPreAssigmentsTakenPassedOnFirst, totalPreAssigmentsNotTakenPassedOnFirstPostAssessment];
  const notPassedData = [
    totalPreAssigmentsTaken - totalPreAssigmentsTakenPassedOnFirst,
    totalPreAssigmentsNotTaken - totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
  ];

  const assessments = [totalPreAssigmentsTaken, totalPreAssigmentsNotTaken];
  const widths = assessments.map(a => calculateWidth(a, totalAssessments, totalHeight));

  const passedSeriesData = generateSeries(passedData, widths);
  const notPassedSeriesData = generateSeries(notPassedData, widths);

  const barchartExporting = defaultExport(downloadCSV, downloadXLSX);

  const barchartOptions: Highcharts.Options = {
    ...defaultStyle,
    ...barchartExporting,
    chart: {
      type: "bar",
      /* tslint:disable-next-line */
      height: widths.includes(0) ? totalHeight : totalHeight + additionalHeight,
      marginTop: widths.includes(0) ? topMargin : 0,
      marginRight: rightMargin,
    },
    legend: {
      enabled: true,
      itemStyle: {
        fontSize: "1.4rem",
      },
      reversed: false,
      x: 40,
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y !== 0 ? this.y : "";
          },
          // centering labels when there is only one bar
          y: widths.includes(0) ? -50 : undefined,
          ...defaultLabel,
        },
      },
      series: {
        stacking: "percent",
        /* tslint:disable-next-line */
        cursor: "pointer",
        point: {
          events: {
            click() {
              const status = this.series.name === "Correct" ? AssessmentStatus.Passed : AssessmentStatus.NotPassed;
              filterFunction(status)(this.category as AssessmentType);
            },
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          chartOptions: {
            legend: {
              enabled: true,
            },
          },
          condition: {
            maxWidth: 500,
          },
        },
      ],
    },
    series: [
      {
        name: "Correct",
        /* tslint:disable-next-line */
        data: passedSeriesData,
        type: "bar",
        color: createGradient(colors.chartGreen70, colors.chartGreen100),
      },
      {
        name: "Incorrect",
        /* tslint:disable-next-line */
        data: notPassedSeriesData,
        type: "bar",
        color: createGradient(colors.chartDarkBlue100, colors.chartDarkBlue70),
      },
    ],
    title: {
      text: "",
    },
    tooltip: {
      outside: true,
      shape: "square",
      formatter() {
        return (
          labelColor(this.color) +
          this.series.name +
          ": <strong>" +
          Math.round(this.percentage) +
          "% / " +
          this.y +
          "</b>"
        );
      },
      positioner(...params) {
        return centerHorizontalBarTooltip(this.chart)(...params);
      },
      ...defaultTooltipSize,
    },
    xAxis: {
      ...defaultAxisLabels("left", {
        formatter() {
          return this.value === AssessmentType.PreAssessment ? "Pre-Assessment" : "Lesson First";
        },
      }),
      categories: [AssessmentType.PreAssessment, AssessmentType.FirstPostAssessment],
      lineColor: "white",
    },
    yAxis: {
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      min: 0,
      title: {
        text: "",
      },
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={barchartOptions}
      containerProps={{ style: { display: "flex", alignContent: "center", width: "100%" } }}
    />
  );
};

const AssessmentChart: React.FC<IAssessmentsGraphProps> = ({
  assessmentTotals,
  downloadCSV,
  downloadXLSX,
  filterFunction,
}) => {
  return (
    <AssessmentsChartContainer>
      <PieChartContainer data-testid="assessment-piechart">
        <AssessmentPieChart assessmentTotals={assessmentTotals} />
      </PieChartContainer>
      <BarChartContainer data-testid="assessment-barchart">
        <AssessmentBarChart
          assessmentTotals={assessmentTotals}
          downloadCSV={downloadCSV}
          downloadXLSX={downloadXLSX}
          filterFunction={filterFunction}
        />
      </BarChartContainer>
    </AssessmentsChartContainer>
  );
};

export default AssessmentChart;
